import { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from "react-i18next";
import { Link } from 'react-router-dom';

import { Table, TableBody, TableCell, TableContainer, TableRow, Chip, TableHead } from '@mui/material';
import { useEnumApi } from 'contexts/EnumApiContext';
import MainCard from 'components/MainCard';
import StatusChart from 'components/inner/chart/StatusChart';
import { DashboardAreaStackChart } from 'components/inner/chart/DashboardAreaStackChart';
import { getDashboardCountEquipmentSummary, getDashboardCountGroupByStatus, getDashboardStatusByChinaRegionPivotByStatus } from 'components/axios/dashboardAxios';
import { getEquipmentAlertsByQueryString } from 'components/axios/equipmentAlertsAxios';
import AlertListCard from 'components/inner/mainCard/AlertListCard';
import { useQuery } from '@tanstack/react-query';
import EchartsYihaiMap from 'components/EchartsYihaiMap';
import StatusIncludeTotalPercentChart from 'components/inner/chart/StatusIncludeTotalPercentChart';

const DELAY_TIME = Number(process.env.REACT_APP_DELAY_TIME) || 60000;

function DashboardYihaiChinaMonitering() {
  const { getEnumValueByEnumName } = useEnumApi();
  const { t, i18n } = useTranslation();

  const EquipmentFaultStatusList = getEnumValueByEnumName("EquipmentFaultStatus");
  // const [statusDatasByProvince, setStatusDatasByProvince] = useState(null);
  // const [statusDatasTotalByProvince, setStatusDatasTotalByProvince] = useState(null);
  const [pvTab, setPvTab] = useState('000000');
  const [province, setProvince] = useState({code:'000000', name:''});

  useEffect(() => {
    document.body.className = 'grid_bg';
    return () => {
      console.log('out page')
      document.body.className = '';
    };
  }, []);

  const { data } = useQuery({
    queryKey: ['yihai-dashboard', i18n.language],

    queryFn: async () => {
      const alertParam = {
        page: 0,
        size: 12,
        "equipment.iotInstlYn": true,
        "equipment.opStatus": "O"
      };

      let addQueryString = "&sort=status,asc&sort=alertedAt,desc";

      let queryString = Object.entries(alertParam)
        .map(([key, value]) => value && key + '=' + value)
        .filter(v => v)
        .join('&');

      queryString += addQueryString;

      const [
        statusData,
        equipmentAlerts,
      ] = await Promise.all([
        getDashboardCountGroupByStatus(),
        // getDashboardCountGroupByStatus({ iotInstlYn: true }),
        getEquipmentAlertsByQueryString("?" + queryString),
      ]);

      return {
        statusData,
        equipmentAlerts,
      };
    },

    refetchInterval: DELAY_TIME,

    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // 🔥 탭 안 보여도 멈추지 않음(기본값은 false)
    refetchIntervalInBackground: true,
    
    // placeholderData: previousData => previousData,

    staleTime: DELAY_TIME,
  });

  const { data : provinceData } = useQuery({
    queryKey: ['yihai-dashboard-province', i18n.language],
    queryFn: async () => {
      const [
        provinceStatuss,
      ] = await Promise.all([
        getDashboardStatusByChinaRegionPivotByStatus(),
      ]);

      return provinceStatuss || [];
    },

    refetchInterval: DELAY_TIME,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // 🔥 탭 안 보여도 멈추지 않음(기본값은 false)
    refetchIntervalInBackground: true,
    // placeholderData: previousData => previousData,
    staleTime: DELAY_TIME,
  });

  const { data : provinceCountSummary } = useQuery({
    queryKey: ['yihai-dashboard-province-summary', i18n.language, province.code],
    queryFn: async () => {
      const param = {
        provinceCode : province?.code != '000000' ? province.code : ''
      }

      const [
        countSummary
      ] = await Promise.all([
        getDashboardCountEquipmentSummary(param)
      ]);

      return countSummary;
      
    },

    refetchInterval: DELAY_TIME,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // 🔥 탭 안 보여도 멈추지 않음(기본값은 false)
    refetchIntervalInBackground: true,
    // placeholderData: previousData => previousData,
    staleTime: 0,
  });
  const countSummary = provinceCountSummary;

  // 3️⃣ 💡 [핵심] province State 변경 시 즉시 동기적(Synchronous)으로 필터링 & 합계 계산
  // 클릭 즉시 useMemo가 비동기 대기 없이 렌더링 단계에서 화면을 바로 갱신합니다.
  const { statusDatasByProvince, statusDatasTotalByProvince } = useMemo(() => {
    const provinceStatuss = provinceData || [];

    if (provinceStatuss.length === 0) {
      return { statusDatasByProvince: null, statusDatasTotalByProvince: null };
    }

    // 1. 선택된 지역 필터링
    const filteredList = (!province || province.code === '000000')
      ? provinceStatuss
      : provinceStatuss.filter(pv => pv.provinceCode === province.code);

    // 2. Total Sum 계산
    const isAll = !province?.code || province.code === '000000';
    const targetData = isAll
      ? provinceStatuss
      : provinceStatuss.filter(item => item.provinceCode === province.code);

    const totals = targetData.reduce(
      (acc, cur) => {
        acc.countO1N += cur.countO1N || 0;
        acc.countO2I += cur.countO2I || 0;
        acc.countO3A += cur.countO3A || 0;
        return acc;
      },
      { countO1N: 0, countO2I: 0, countO3A: 0 }
    );

    let totalData = null;
    if (!isAll && targetData.length > 0) {
      const matched = targetData[0];
      totalData = {
        managementCompanyId: matched.managementCompanyId,
        managementCompanyName: matched.managementCompanyName,
        provinceCode: matched.provinceCode,
        provinceName: matched.provinceName,
        zoneId: matched.zoneId,
        zoneName: matched.zoneName,
        ...totals
      };
    } else {
      totalData = {
        managementCompanyId: "",
        managementCompanyName: "",
        provinceCode: "000000",
        provinceName: t('message.common.total'),
        zoneId: "",
        zoneName: "",
        ...totals
      };
    }

    return {
      statusDatasByProvince: filteredList,
      statusDatasTotalByProvince: totalData,
    };
  }, [provinceData, province, t]);
  
  // const provinceStatuss = provinceData?.provinceStatuss;
  const alertDatas = data?.equipmentAlerts;
  const statusDatas = data?.statusData;

  const handleSelect = ({ name, code }) => {
    // console.log(name, code);
    setPvTab(code);
    setProvince({code,name});
  };

  let main_container_css = 'main_container1_yi';

  return (
    <div className={`main_container main_container1 ${main_container_css} cn_main_container`}>
      <div className='main_dashboard'>
        <div className='main_dash_left'>
          <MainCard content={false} className="cn_status_box">
            <div className='alert_phrase'>
              <div style={{display: "flex", alignItems: "center"}}>
                <div style={{padding:"5px 10px"}}>
                  <span className='bullet'><img src={`${process.env.PUBLIC_URL}/img/icon/dashboard.png`} alt='' /></span>&nbsp;
                  <Chip label={t('message.common.chip.dashboard.alert.list')} color="primary" />
                </div>
              </div>

              <div className="charts_box2">
                <AlertListCard equipmentAlerts={alertDatas?._embedded?.equipmentAlerts} target='md' />
              </div>
            </div>
          </MainCard>

          <div className='cn_status_box'>
            <MainCard content={false} style={{marginTop: "10px"}}>
              <div className='alert_phrase'>
                <div style={{display: "flex", alignItems: "center"}}>
                  <div style={{padding:"5px 10px"}}>
                    <span className='bullet'><img src={`${process.env.PUBLIC_URL}/img/icon/dashboard.png`} alt='' /></span>&nbsp;
                    <Chip label={t('message.common.chip.situation.status')} color="primary" />
                  </div>
                </div>

                <div className="charts_box2">
                  {(EquipmentFaultStatusList) && <StatusChart EquipmentFaultStatusList={EquipmentFaultStatusList} statusDatas={statusDatas} />}
                </div>
              </div>
            </MainCard>

            <MainCard content={false} style={{position:"relative", boxSizing: "border-box", marginTop: "15px"}}>
              <div className='alert_phrase'>
                <div style={{display: "flex", alignItems: "center"}}>
                  <div style={{padding:"5px 10px"}}>
                    <span className='bullet'><img src={`${process.env.PUBLIC_URL}/img/icon/dashboard.png`} alt='' /></span>&nbsp;
                    <Chip label={t('message.common.chip.level3.diagnosis.status')} color="primary" />
                  </div>
                </div>
                
                <div className="charts_box2">
                  <DashboardAreaStackChart />
                </div>
              </div>
            </MainCard>
          </div>
        </div>

        <div className='main_dash_center'>
          <EchartsYihaiMap statusDatasByProvince={provinceData} selectedProvinceCode={province.code} onSelect={handleSelect} />
        </div>

        <div className='main_dash_right'>
          <MainCard content={false} className="cn_status_box">
            <div className='alert_phrase'>
              <div style={{display: "flex", alignItems: "center"}}>
                <div style={{padding:"5px 10px"}}>
                  <span className='bullet'><img src={`${process.env.PUBLIC_URL}/img/icon/dashboard.png`} alt='' /></span>&nbsp;
                  <Chip label={t('message.common.chip.registration.status')+(pvTab === '000000' ? '' : ' - '+province?.name)} color="default" />
                </div>
              </div>

              <TableContainer className="table_part">
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('message.common.subtitle.province')}</TableCell>
                      <TableCell>{t('message.common.subtitle.company')}</TableCell>
                      <TableCell>{t('message.common.btn.equipment')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>{countSummary?.provinceCount ?? 0}</TableCell>
                      <TableCell>{(countSummary?.maintenanceCompanyCount ?? 0)
                        +(countSummary?.managementCompanyCount ?? 0)+(countSummary?.manufacturingCompanyCount ?? 0)}</TableCell>
                      <TableCell>{countSummary?.equipmentCount ?? 0}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </MainCard>

          <MainCard content={false} className="cn_status_box" style={{marginTop: "10px"}}>
            <div className='alert_phrase'>
              <div style={{display: "flex", alignItems: "center"}}>
                <div style={{padding:"5px 10px"}}>
                  <span className='bullet'><img src={`${process.env.PUBLIC_URL}/img/icon/dashboard.png`} alt='' /></span>&nbsp;
                  <Chip label={pvTab === '000000' ? t('message.common.chip.province') : (pvTab === '000000' ? '' : province?.name)} color="primary" />
                </div>
              </div>

              {/* <div style={{display: "flex", alignItems: "center"}}>
                <div style={{padding:"5px 10px"}}>
                  &nbsp;&nbsp;*&nbsp;
                  <Chip label={t('message.common.chip.equipment.status.overview')} color="primary" />
                </div>
              </div> */}

              <div className="charts_box2">
                {(EquipmentFaultStatusList && statusDatasTotalByProvince) && 
                  <StatusIncludeTotalPercentChart key={`total-chart-${province.code}`} EquipmentFaultStatusList={EquipmentFaultStatusList} statusDatas={statusDatasTotalByProvince} />}
              </div>
            </div>

            <div style={styles.container}>
            {statusDatasByProvince && statusDatasByProvince.map((datas, i)=> {
              // const zoneNameBase = datas.managementCompanyName + '-' + datas.zoneName;
              const zoneNameBase = datas.zoneName;
              const zoneName = zoneNameBase.length > 7 ? zoneNameBase.substring(0,6)+"..." : zoneNameBase;
              return (
              <div key={i} style={styles.item}>
                <div className='alert_phrase' style={{marginTop: "10px"}}>
                  <div>
                    <div>
                      &nbsp;&nbsp;*&nbsp;
                      <Link to={`/dashboard/sub?zId=${datas.zoneId}`}><Chip label={zoneName} color="primary" style={{ cursor: 'pointer' }} /></Link>
                    </div>
                  </div>

                  <div className="charts_box2" style={{padding:"unset"}}>
                    {(EquipmentFaultStatusList && datas) && 
                      <StatusIncludeTotalPercentChart key={`sub-chart-${datas.zoneId || i}-${province.code}`} EquipmentFaultStatusList={EquipmentFaultStatusList} statusDatas={datas} type="total" />}
                  </div>
                </div>
              </div>
              )
            })}
              <div style={styles.item}>
                <div className='alert_phrase' style={{marginTop: "10px"}}>
                  <div>
                    <div>
                      &nbsp;&nbsp;*&nbsp;
                      <Link to={`https://nt.metapredic.cn:6100/`} target="_blank" rel="noopener noreferrer">
                        <Chip label={'南通轨道交通'} color="primary" style={{ cursor: 'pointer' }} />
                      </Link>
                    </div>
                  </div>

                  <div className="charts_box2" style={{padding:"unset", height:"105px"}}>
                    <img src={`${process.env.PUBLIC_URL}/img/open_in_new.png`} />
                  </div>
                  <div style={{textAlign:"center",fontSize:"16px"}}>
                    私有化
                  </div>
                </div>
              </div>
            
            </div>
            
          </MainCard>
          
        </div>
      </div>
    </div>
  )
}

const styles = {
  container: {
    display: "flex",
    flexWrap: "wrap",   // 줄바꿈 허용
    gap: "12px",        // 간격
  },
  item: {
    flex: "0 0 calc(33.333% - 10px)", // 한 줄에 3개
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
};
export default DashboardYihaiChinaMonitering;
